/**
 * Top Holdings Table Component
 * Displays top holdings aggregated across all portfolios
 * Click row to drill-down into client breakdown
 */

import React, { useState } from 'react';
import { ChevronUp, ChevronDown, AlertCircle, TrendingUp } from 'lucide-react';
import { TopHolding } from '../../hooks/useMyBookData';
import Badge from './atoms/Badge';
import EmptyState from './atoms/EmptyState';

interface TopHoldingsTableProps {
  holdings: TopHolding[];
  onHoldingClick?: (holding: TopHolding) => void;
  loading?: boolean;
}

const TopHoldingsTable: React.FC<TopHoldingsTableProps> = ({
  holdings,
  onHoldingClick,
  loading
}) => {
  const [sortField, setSortField] = useState<keyof TopHolding>('totalValue');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');

  // Format currency
  const formatCurrency = (value: number) => {
    const millions = value / 1000000;
    return millions >= 1
      ? `CHF ${millions.toFixed(2)}M`
      : `CHF ${(value / 1000).toFixed(0)}K`;
  };

  // Format percentage
  const formatPercent = (value: number) => {
    return `${value.toFixed(2)}%`;
  };

  // Handle sort
  const handleSort = (field: keyof TopHolding) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('desc');
    }
  };

  // Sort holdings
  const sortedHoldings = [...holdings].sort((a, b) => {
    const aValue = a[sortField];
    const bValue = b[sortField];

    if (typeof aValue === 'number' && typeof bValue === 'number') {
      return sortDirection === 'asc' ? aValue - bValue : bValue - aValue;
    }

    const aStr = String(aValue);
    const bStr = String(bValue);
    return sortDirection === 'asc'
      ? aStr.localeCompare(bStr)
      : bStr.localeCompare(aStr);
  });

  // Get concentration warning (>20%)
  const getConcentrationWarning = (percentOfBook: number) => {
    if (percentOfBook > 20) return 'critical';
    if (percentOfBook > 10) return 'warning';
    return null;
  };

  // Render sort icon
  const renderSortIcon = (field: keyof TopHolding) => {
    if (sortField !== field) return null;
    return sortDirection === 'asc' ? (
      <ChevronUp className="w-4 h-4 ml-1" />
    ) : (
      <ChevronDown className="w-4 h-4 ml-1" />
    );
  };

  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow-lg p-6 mb-6">
        <div className="animate-pulse space-y-3">
          <div className="h-6 bg-gray-200 rounded w-1/4"></div>
          <div className="h-10 bg-gray-200 rounded"></div>
          <div className="h-10 bg-gray-200 rounded"></div>
          <div className="h-10 bg-gray-200 rounded"></div>
        </div>
      </div>
    );
  }

  if (holdings.length === 0) {
    return (
      <div className="bg-white rounded-lg shadow-lg p-6 mb-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Top Holdings</h3>
        <EmptyState
          type="no-results"
          title="No Holdings Found"
          description="No holdings match the selected criteria."
        />
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow-md overflow-hidden mb-6">
      {/* Header */}
      <div className="px-6 py-4 border-b border-gray-200">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-semibold text-[#002B45]">
            Top Holdings Across Book
          </h3>
          <div className="flex items-center gap-2 text-sm text-gray-500">
            <TrendingUp className="w-4 h-4" />
            <span>{holdings.length} holdings</span>
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th
                className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                onClick={() => handleSort('isin')}
              >
                <div className="flex items-center">
                  ISIN
                  {renderSortIcon('isin')}
                </div>
              </th>
              <th
                className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                onClick={() => handleSort('securityName')}
              >
                <div className="flex items-center">
                  Security
                  {renderSortIcon('securityName')}
                </div>
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Type / Sector
              </th>
              <th
                className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                onClick={() => handleSort('totalValue')}
              >
                <div className="flex items-center justify-end">
                  Total Value
                  {renderSortIcon('totalValue')}
                </div>
              </th>
              <th
                className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                onClick={() => handleSort('percentOfBook')}
              >
                <div className="flex items-center justify-end">
                  % of Book
                  {renderSortIcon('percentOfBook')}
                </div>
              </th>
              <th
                className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                onClick={() => handleSort('clientCount')}
              >
                <div className="flex items-center justify-center">
                  # Clients
                  {renderSortIcon('clientCount')}
                </div>
              </th>
              <th
                className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                onClick={() => handleSort('portfolioCount')}
              >
                <div className="flex items-center justify-center">
                  # Portfolios
                  {renderSortIcon('portfolioCount')}
                </div>
              </th>
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                Impact if -10%
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {sortedHoldings.map((holding) => {
              const concentrationLevel = getConcentrationWarning(holding.percentOfBook);

              return (
                <tr
                  key={holding.isin}
                  onClick={() => onHoldingClick?.(holding)}
                  className="hover:bg-gray-50 cursor-pointer transition-colors"
                >
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-mono text-gray-900">
                      {holding.isin}
                    </div>
                    <div className="text-xs text-gray-500">
                      {holding.currency}
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="text-sm font-medium text-gray-900">
                      {holding.securityName}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex flex-col gap-1">
                      <Badge
                        label={holding.securityType}
                        variant="info"
                        size="sm"
                      />
                      <span className="text-xs text-gray-500">{holding.sector}</span>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-right font-medium text-gray-900">
                    {formatCurrency(holding.totalValue)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right">
                    <div className="flex items-center justify-end gap-2">
                      <span className="text-sm font-medium text-gray-900">
                        {formatPercent(holding.percentOfBook)}
                      </span>
                      {concentrationLevel && (
                        <div
                          className="flex items-center"
                          title={
                            concentrationLevel === 'critical'
                              ? 'High concentration risk (>20%)'
                              : 'Moderate concentration (>10%)'
                          }
                        >
                          <AlertCircle
                            className={`w-4 h-4 ${
                              concentrationLevel === 'critical'
                                ? 'text-red-500'
                                : 'text-yellow-500'
                            }`}
                          />
                        </div>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-center">
                    <span className="inline-flex items-center justify-center px-2 py-1 text-xs font-medium bg-blue-100 text-blue-800 rounded-full">
                      {holding.clientCount}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-center">
                    <span className="inline-flex items-center justify-center px-2 py-1 text-xs font-medium bg-purple-100 text-purple-800 rounded-full">
                      {holding.portfolioCount}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right">
                    <div className="text-sm font-medium text-red-600">
                      -{formatCurrency(holding.totalValue * 0.1)}
                    </div>
                    <div className="text-xs text-gray-500">
                      {holding.clientCount} {holding.clientCount === 1 ? 'client' : 'clients'} affected
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Footer hint */}
      <div className="px-6 py-3 bg-gray-50 border-t text-xs text-gray-500 text-center">
        Click on any holding to see client breakdown
      </div>
    </div>
  );
};

export default TopHoldingsTable;
